import type { Action } from "@/components/MenuButton";

export function resolveParticipationReturnPath(returnUrl: string | null, activityTypeId: string) {
  return returnUrl ? decodeURIComponent(returnUrl) : `/activities/activity-types/${activityTypeId}/participants`;
}

export function buildParticipationPath({
  activityTypeId,
  participantId,
  pathname,
  search
}: {
  activityTypeId: string;
  participantId: string;
  pathname: string;
  search: string;
}) {
  const encodedReturnUrl = encodeURIComponent(`${pathname}${search}`);
  return `/activities/activity-types/${activityTypeId}/participants/${participantId}?returnUrl=${encodedReturnUrl}`;
}

export function buildParticipationActions({
  canManageParticipations,
  isTypeLevelParticipation,
  isCcmsLegacyCredit = false,
  canManageLegacyPdrCredits = false,
  hasCredit,
  canEdit,
  canDelete,
  onEdit,
  onDelete
}: {
  canManageParticipations: boolean;
  isTypeLevelParticipation: boolean;
  isCcmsLegacyCredit?: boolean;
  canManageLegacyPdrCredits?: boolean;
  hasCredit: boolean;
  canEdit?: boolean | null;
  canDelete?: boolean | null;
  onEdit: () => void;
  onDelete: () => void;
}): Action[] {
  const { canShowEdit, canShowDelete } = getParticipationActionPermissions({
    canManageParticipations,
    isTypeLevelParticipation,
    isCcmsLegacyCredit,
    canManageLegacyPdrCredits,
    hasCredit,
    canEdit,
    canDelete
  });

  if (!canShowEdit && !canShowDelete) {
    return [];
  }

  return [
    ...(canShowEdit
      ? [
          {
            text: "Edit Participation",
            onClick: onEdit
          } satisfies Action
        ]
      : []),
    ...(canShowDelete
      ? [
          {
            text: "Delete Participation",
            destructive: true,
            iconName: "delete",
            onClick: onDelete
          } satisfies Action
        ]
      : [])
  ];
}

export function getParticipationActionPermissions({
  canManageParticipations,
  isTypeLevelParticipation,
  isCcmsLegacyCredit = false,
  canManageLegacyPdrCredits = false,
  hasCredit,
  canEdit,
  canDelete
}: {
  canManageParticipations: boolean;
  isTypeLevelParticipation: boolean;
  isCcmsLegacyCredit?: boolean;
  canManageLegacyPdrCredits?: boolean;
  hasCredit: boolean;
  canEdit?: boolean | null;
  canDelete?: boolean | null;
}) {
  if (isCcmsLegacyCredit) {
    const canShowLegacyActions = canManageLegacyPdrCredits && hasCredit;
    return {
      canShowEdit: canShowLegacyActions,
      canShowDelete: canShowLegacyActions
    };
  }

  const hasBackendCanEdit = typeof canEdit === "boolean";
  const hasBackendCanDelete = typeof canDelete === "boolean";

  if (hasBackendCanEdit || hasBackendCanDelete) {
    return {
      canShowEdit: hasBackendCanEdit
        ? !!canEdit && (hasCredit || isTypeLevelParticipation)
        : !isTypeLevelParticipation && canManageParticipations,
      canShowDelete: hasBackendCanDelete
        ? !!canDelete && (hasCredit || isTypeLevelParticipation)
        : !isTypeLevelParticipation && canManageParticipations
    };
  }

  if (!hasCredit) {
    return { canShowEdit: false, canShowDelete: false };
  }

  return {
    canShowEdit: !isTypeLevelParticipation && canManageParticipations,
    canShowDelete: !isTypeLevelParticipation && canManageParticipations
  };
}
